import { Link, Outlet, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { UserMenu } from './Header';
import { FilterDropdown } from './FilterDropdown';
import { SortDropdown } from './SortDropdown';
import { PithLogo } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { FILTER_OPTIONS, type FilterType } from '@/lib/filters';
import { cn } from '@/lib/utils';

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter') as FilterType;
  const isDashboard = location.pathname === '/dashboard';
  const isLinkDetail = location.pathname.startsWith('/links/');
  const isMobile = useIsMobile();

  const handleFilterChange = (value: FilterType) => {
    if (value) {
      setSearchParams({ filter: value });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Header */}
      <header className="flex h-16 shrink-0 items-center gap-4 px-4 safe-area-top">
        <Link to="/dashboard">
          <PithLogo size="md" />
        </Link>
        {isLinkDetail && (
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        )}
        {isDashboard && !isMobile && <FilterDropdown />}
        <div className="flex-1" />
        {isDashboard && <SortDropdown />}
        <UserMenu />
      </header>

      {/* Main content */}
      <main className={cn('flex-1 overflow-auto', isMobile && isDashboard && 'pb-20')}>
        <div className={cn('mx-auto max-w-4xl', isMobile && isDashboard ? '' : 'px-4')}>
          <Outlet />
        </div>
      </main>

      {/* Mobile Tab Bar - iOS-style bottom navigation */}
      {isMobile && isDashboard && (
        <nav className="fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur-sm safe-area-bottom">
          <div className="flex h-16 items-center justify-around">
            {FILTER_OPTIONS.map((option) => {
              const isActive = filter === option.value;
              const TabIcon = option.icon;
              return (
                <button
                  key={option.label}
                  onClick={() => handleFilterChange(option.value)}
                  className={cn(
                    'flex flex-1 flex-col items-center justify-center gap-1 py-2 transition-colors',
                    // 44pt minimum touch target
                    'min-h-[44px]',
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  )}
                >
                  <TabIcon className={cn('h-5 w-5', isActive && 'fill-current')} />
                  <span className="text-xs font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
